"use client";

import React, { useEffect, useRef, useState } from "react";
import { Terminal, Cpu, Database } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const faqData = [{
  query: "Wie funktioniert die Miete der Mehrwegbecher?",
  response: "Ganz einfach: Sie fragen die gewünschte Menge an, wir liefern die sauberen Becher pünktlich zu Ihrem Event und holen sie nach der Veranstaltung wieder ab. Die Abrechnung erfolgt transparent nach verbrauchter Menge."
}, {
  query: "Bieten Sie auch die Reinigung der Becher an?",
  response: "Ja, wir verfügen über eine professionelle Reinigungsanlage in Kärnten. Wir übernehmen die hygienische Reinigung nach höchsten Standards, damit Sie sich voll und ganz auf Ihr Event konzentrieren können."
}, {
  query: "Können die Becher individuell bedruckt werden?",
  response: "Absolut. Wir bieten hochwertigen individuellen Druck für Kaufbecher an. So wird Ihr Mehrwegbecher zum perfekten Werbeträger für Ihren Verein, Ihr Festival oder Ihr Unternehmen."
}, {
  query: "Was passiert bei Verlust oder Beschädigung?",
  response: "Ein gewisser Schwund ist bei Großveranstaltungen normal. Fehlende oder stark beschädigte Becher werden zu einem vorher vereinbarten Stückpreis verrechnet. Wir beraten Sie gerne zu Pfandsystemen."
}, {
  query: "Liefern Sie auch außerhalb von Kärnten?",
  response: "Unser Fokus liegt auf regionalem Service in Kärnten und Steiermark, um Transportwege kurz zu halten. Auf Anfrage und je nach Projektgröße beliefern wir jedoch auch andere Regionen."
}];
export default function RobotAgentFaq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="223102" id="häufige-fragen" className="bg-accent text-foreground py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-primary/5 blur-[100px] rounded-full pointer-events-none" />

      <div ref={ref} className={`container mx-auto max-w-4xl relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="mb-16 text-center space-y-6">
          <div className="inline-flex items-center justify-center p-3 rounded-full bg-card border border-border shadow-lg shadow-primary/5 relative group">
            <div className="absolute inset-0 bg-primary/10 blur-md rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <Cpu className="w-8 h-8 text-primary relative z-10" />
          </div>
          
          <div className="space-y-2">
            <h2 className="text-3xl md:text-5xl font-bold font-default tracking-tighter text-secondary">
              Häufig gestellte Fragen
            </h2>
            <div className="flex items-center justify-center gap-2 text-muted-foreground font-default text-sm uppercase tracking-widest">
              <Database className="w-4 h-4" />
              <span>Alles Wissenswerte zu unserem Service</span>
              <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-1" />
            </div>
          </div>
        </div>

        <div className="bg-card/60 backdrop-blur-xl border border-border rounded-2xl p-4 md:p-8 shadow-xl">
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqData.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border border-border bg-background/80 rounded-xl overflow-hidden data-[state=open]:border-primary/30 data-[state=open]:bg-white transition-all duration-300 group">
                <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-accent/50 transition-colors">
                  <div className="flex items-center gap-3 text-left">
                    <Terminal className="w-4 h-4 text-primary opacity-70 group-data-[state=open]:opacity-100 transition-opacity" />
                    <span className="text-secondary font-default text-base md:text-lg font-bold tracking-tight">
                      {faq.query}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-5 pt-1">
                  <div className="flex gap-4 items-start pl-7">
                    <div className="w-px h-full min-h-[2.5rem] bg-gradient-to-b from-primary to-transparent mt-1 shrink-0" />
                    <p className="text-muted-foreground font-default text-sm md:text-base leading-relaxed">
                      {faq.response}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}